import { createApp } from "vue";
import { createPinia } from "pinia";
import App from "./App.vue";
import router from "./router";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "bootstrap/dist/js/bootstrap.min.js";
import "./assets/scss/app.scss";
import Notifications from "@kyvg/vue3-notification";
import "core-js/features/object/has-own";

import VueFeather from "vue-feather";
import { createI18n } from "vue-i18n";
import Breadcrumbs from "./components/commen/block/header/BreadCrumb.vue";
import Auth from "./API/Auth";
import DirectiveScannerPlugin from "./directiveScannerPlugin";
import language from "@/API/Language/Language";
import translate from "@/API/Translate/Translate";
import SystemSetting from "./API/SystemSetting/SystemSetting";
import { applyFaviconFromGeneralSettings } from "./utils/favicon";
import {
  createI18nEditorPlugin,
  registerI18nContextMenu,
  setI18nEditMode,
} from "./plugins/i18nEditorPlugin";
import I18nEditorModal from "./components/I18nEditorModal.vue";
import I18nText from "./components/I18nText.vue";

import "vuetify/styles";
import { createVuetify } from "vuetify";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";
import "@/assets/styles/style.css";

const vuetify = createVuetify({
  components,
  directives,
});

// TypeScript interfaces
interface LanguageItem {
  id: string;
  code: string;
  name: string;
  order: number;
  rtl: string;
  flag: string;
}

interface Messages {
  [key: string]: {
    [key: string]: {
      [key: string]: string;
    };
  };
}

interface TranslationEntry {
  [key: string]: string;
}

interface TranslationGroup {
  name: string;
  en: TranslationEntry[];
  ar: TranslationEntry[];
  [key: string]: any;
  id: string;
  created_at: string;
  updated_at: string;
}

interface ProcessedTranslations {
  [key: string]: {
    // language code
    [key: string]: {
      // section name
      [key: string]: string; // translations
    };
  };
}

// Initialize APIs
const languageApi = new language();
const translateApi = new translate();
const systemSettingApi = new SystemSetting();
// Function to process translations from API
function processTranslations(
  translations: TranslationGroup[],
): ProcessedTranslations {
  const processed: ProcessedTranslations = {};

  translations.forEach((group) => {
    // Get all language codes from the group (excluding non-language properties)
    const langCodes = Object.keys(group).filter(
      (key) => !["name", "id", "created_at", "updated_at"].includes(key),
    );

    // Process each language code
    langCodes.forEach((langCode) => {
      if (!processed[langCode]) {
        processed[langCode] = {};
      }

      if (!processed[langCode][group.name]) {
        processed[langCode][group.name] = {};
      }

      // Process translations for this language and group
      const translations = group[langCode];
      if (Array.isArray(translations)) {
        translations.forEach((trans) => {
          Object.entries(trans).forEach(([key, value]) => {
            if (typeof value === "string") {
              processed[langCode][group.name][key] = value;
            }
          });
        });
      }
    });
  });

  return processed;
}

// Function to load language messages
async function loadLanguages(): Promise<{
  messages: Messages;
  defaultLocale: string;
  editModeEnabled: boolean;
}> {
  try {
    // Get both languages configuration and translations
    // const [languages, translations] = await Promise.all([
    //     languageApi.getAll() as Promise<LanguageItem[]>,
    //     translateApi.getAll() as Promise<TranslationGroup[]>
    // ]);

    // // Sort languages by order
    //   const sortedLanguages = languages.sort((a, b) => Number(a.order) - Number(b.order));
    const sortedLanguages = [
      localStorage.getItem("selectedLanguageCode") || "en",
    ];

    const translations = (await translateApi.getAll({
      select: `id|name|${sortedLanguages[0]}`,
    })) as TranslationGroup[];

    const systemSettingResponse = await systemSettingApi.getAll({
      filter: "type|editModeTranslation|=",
    });
    let editModeEnabled = false;
    if (systemSettingResponse && systemSettingResponse.length > 0) {
      const settingValue = systemSettingResponse[0].value;
      editModeEnabled = settingValue == "true" || settingValue == true;
      window.__I18N_EDIT_MODE__ = editModeEnabled;
    }
    // Process translations into organized structure
    const processedTranslations = processTranslations(translations);

    // Create messages object
    const messages: Messages = {};
    for (const lang of sortedLanguages) {
      // Use only the base language code (e.g., 'en' from 'en-US')
      const langCode = lang.toLowerCase().split("-")[0];
      if (processedTranslations[langCode]) {
        messages[lang.toLowerCase()] = processedTranslations[langCode];
      } else {
        console.warn(`No translations found for language: ${lang} (${lang})`);
        messages[lang.toLowerCase()] = {}; // Empty translations object as fallback
      }
    }
    // Get saved language from localStorage, fallback to 'en'
    const savedLangCode = localStorage.getItem("selectedLanguageCode");
    const defaultLocale =
      savedLangCode && messages[savedLangCode] ? savedLangCode : "en";
    return {
      messages,
      defaultLocale,
      editModeEnabled,
    };
  } catch (err) {
    console.error("Failed to load languages:", err);
    // Fallback to English if API fails
    const English = await import("@/locales/en.json");
    // Ensure the structure matches Messages interface
    const englishMessages: { [key: string]: { [key: string]: string } } = {};
    Object.entries(English.default).forEach(([section, value]) => {
      // If value is an object, assign directly; if it's a string, wrap it
      englishMessages[section] =
        typeof value === "object" && value !== null
          ? (value as { [key: string]: string })
          : { value: value as string };
    });
    return {
      messages: { English: englishMessages },
      defaultLocale: "en",
      editModeEnabled: false,
    };
  }
}

let i18n: ReturnType<typeof createI18n>;

// Initialize app after loading languages
async function initializeApp() {
  try {
    // Initialize Auth first
    Auth.run();

    // Load languages only if user is logged in
    const isLoggedIn = await Auth.loggedIn();
    const { messages, defaultLocale, editModeEnabled } = isLoggedIn
      ? await (async () => {
          const loaded = await loadLanguages();
          await applyFaviconFromGeneralSettings();
          return loaded;
        })()
      : await (async () => {
          try {
            const English = await import("@/locales/en.json");
            const englishMessages: { [key: string]: { [key: string]: string } } = {};
            Object.entries(English.default).forEach(([section, value]) => {
              englishMessages[section] =
                typeof value === "object" && value !== null
                  ? (value as { [key: string]: string })
                  : { value: value as string };
            });
            return { messages: { en: englishMessages }, defaultLocale: "en", editModeEnabled: false };
          } catch {
            return { messages: { en: {} }, defaultLocale: "en", editModeEnabled: false };
          }
        })();

    // Create i18n instance
    i18n = createI18n({
      legacy: false,
      locale: defaultLocale,
      messages,
      fallbackLocale: "en",
    });

    // Store messages in window for easy access
    (window as any).__i18nMessages = messages;
    (window as any).__i18nLocale = defaultLocale;

    // Create Vue app
    const app = createApp(App)
      .use(router)
      .use(createPinia())
      .use(i18n)
      .use(vuetify)
      .use(Notifications)
      .component("Breadcrumbs", Breadcrumbs)
      .component("I18nText", I18nText)
      .use(DirectiveScannerPlugin);

    if (isLoggedIn) {
      app
        .use(createI18nEditorPlugin(i18n, { enableEditMode: editModeEnabled }))
        .component("I18nEditorModal", I18nEditorModal);

      // Activate edit mode if enabled in settings
      if (editModeEnabled) {
        setI18nEditMode(true);
      }

      // Register context menu listener for i18n editor
      registerI18nContextMenu((key: string, locale: string, value: string) => {
        if (window.__I18N_EDITOR__) {
          window.__I18N_EDITOR__.openModal(key, locale, value);
        }
      });
    }

    // Make edit mode toggler available in console for testing
    // In production, this should be controlled by user roles
    (window as any).__setI18nEditMode__ = setI18nEditMode;

    // Add debug helper to test context menu
    (window as any).__testI18nEdit__ = (text: string) => {
      console.log("🧪 Testing i18n editor with text:", text);
      if (window.__I18N_EDITOR__) {
        const locale = (i18n.global.locale as any).value || "en";
        window.__I18N_EDITOR__.openModal("test.key", locale, text);
      } else {
        console.error("❌ Modal not ready");
      }
    };

    // Mount the app
    app.mount("#app");

    // Authentication check will be handled by router guards
    // No need to check authentication here as it's handled in router.beforeEach
  } catch (error) {
    console.error("Failed to initialize app:", error);

    // Fallback initialization with basic English
    try {
      const English = await import("@/locales/en.json");
      const englishMessages: { [key: string]: { [key: string]: string } } = {};
      Object.entries(English.default).forEach(([section, value]) => {
        englishMessages[section] =
          typeof value === "object" && value !== null
            ? (value as { [key: string]: string })
            : { value: value as string };
      });

      i18n = createI18n({
        legacy: false,
        locale: "en",
        messages: { en: englishMessages },
        fallbackLocale: "en",
      });

      // Store messages in window for easy access
      (window as any).__i18nMessages = { en: englishMessages };
      (window as any).__i18nLocale = "en";

      const app = createApp(App)
        .use(router)
        .use(createPinia())
        .use(i18n)
        .use(createI18nEditorPlugin(i18n, { enableEditMode: false }))
        .use(vuetify)
        .use(Notifications)
        .component("Breadcrumbs", Breadcrumbs)
        .component("I18nEditorModal", I18nEditorModal)
        .component("I18nText", I18nText)
        .use(DirectiveScannerPlugin);

      // Register context menu listener for i18n editor
      registerI18nContextMenu((key: string, locale: string, value: string) => {
        if (window.__I18N_EDITOR__) {
          window.__I18N_EDITOR__.openModal(key, locale, value);
        }
      });

      // Make edit mode toggler available in console for testing
      (window as any).__setI18nEditMode__ = setI18nEditMode;

      app.mount("#app");
    } catch (fallbackError) {
      console.error("Failed to initialize fallback app:", fallbackError);
    }
  }
}

// Export i18n instance for use in other files
export { i18n };

// Export a function to safely get i18n instance
export function getI18n() {
  return i18n;
}

// Start the application
initializeApp();